import { create } from 'zustand';
import type { User } from '@scrambl/shared';
import { debugLog } from '@/utils/debug';
import { createLogger } from '@/lib/logger';
import { isTauri, getPlatform, desktopSecureStorage } from '@/lib/utils/tauri';

const logger = createLogger('store/auth.store');

// Define the auth state interface
export interface AuthState {
  user: User | null;
isAuthenticated: boolean;
  isLoading: boolean;
  isAuthReady: boolean;
  lastAuthCheckTime: number | null;
  
  // Fetch method
  authFetch: (url: string, options?: RequestInit) => Promise<Response>;
  
  // Auth actions
  setAuth: (user: User | null, isAuthenticated: boolean, isLoading: boolean) => void;
  setIsAuthReady: (ready: boolean) => void;
  waitForAuthReady: () => Promise<void>;
  refreshUser: () => Promise<void>;
  
  // Timezone helper
  getUserTimezone: () => string;
}

// Create a store that bridges the React context to Zustand
export const useAuth = create<AuthState>((set, get) => {
  debugLog('[AuthBridge] Initializing auth store');
  
  // We'll track any ongoing waitForAuthReady promises to avoid creating multiple
  let currentWaitPromise: Promise<void> | null = null;
  let isStoreInitialized = false;
  
  return {
    user: null,
    isAuthenticated: false,
    isLoading: true,
    isAuthReady: false,
    lastAuthCheckTime: null,
    
    // Default implementation of authFetch
    authFetch: async (url: string, options: RequestInit = {}) => {
      // Get the existing headers, if any
      const existingHeaders = options.headers as Record<string, string> || {};
      
      // If auth is not ready yet and this is not an auth-related endpoint, wait
      if (!get().isAuthReady && !url.includes('/auth/')) {
        debugLog('[AuthBridge] Auth not ready, waiting before making request to:', url);
        try {
          await get().waitForAuthReady();
          debugLog('[AuthBridge] Auth now ready, proceeding with request');
        } catch (e) {
          logger.error('[AuthBridge] Error waiting for auth to be ready:', e);
        }
      }
      
      // Add desktop bearer token when running in Tauri.
      const desktopAuthHeader: Record<string, string> = {};
      if (isTauri()) {
        try {
          const tokens = await desktopSecureStorage.getTokens();
          if (tokens?.accessToken) {
            desktopAuthHeader['Authorization'] = `Bearer ${tokens.accessToken}`;
          }
        } catch (error) {
          logger.error('[AuthBridge] Failed to load desktop auth token:', error);
        }
      }

      // Ensure credentials are included to send cookies
      const fetchOptions: RequestInit = {
        ...options,
        credentials: 'include',
        mode: 'cors',
        headers: {
          ...existingHeaders,
          ...desktopAuthHeader,
          'Content-Type': existingHeaders['Content-Type'] || 'application/json',
          'x-platform': getPlatform(),
        },
      };
      
      // Get the API URL from build-time environment
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4005';
      const fullUrl = url.startsWith('http') ? url : `${apiUrl}${url}`;
      debugLog(`[AuthBridge] Fetching from: ${fullUrl}`);
      
      return fetch(fullUrl, fetchOptions);
    },
    
    // Actions
    setAuth: (user, isAuthenticated, isLoading) => {
      const currentState = get();
      
      // Only update if something has changed to avoid unnecessary renders
      if (
        currentState.user !== user || 
        currentState.isAuthenticated !== isAuthenticated || 
        currentState.isLoading !== isLoading || 
        !isStoreInitialized ||
        currentState.user?.setupComplete !== user?.setupComplete ||
        currentState.user?.timezone !== user?.timezone // Add specific check for timezone changes
      ) {
        debugLog('[AuthBridge] Setting auth state:', { 
          userId: user?.id, 
          email: user?.email,
          isAuthenticated, 
          isLoading,
          isFirstInit: !isStoreInitialized,
          setupComplete: user?.setupComplete,
          timezone: user?.timezone
        });
        
        isStoreInitialized = true;
        
        set({
          user,
          isAuthenticated,
          isLoading,
          isAuthReady: !isLoading || currentState.isAuthReady, // Preserve isAuthReady if it's already true
          lastAuthCheckTime: Date.now()
        });
      }
    },
    
    // Set auth ready state separately
    setIsAuthReady: (ready) => {
      // Only update if it's changing to avoid unnecessary renders
      if (get().isAuthReady !== ready) {
        debugLog('[AuthBridge] Setting auth ready state:', ready);
        set({ isAuthReady: ready });
      }
    },
    
    // Force refresh user data from backend
    refreshUser: async () => {
      try {
        debugLog('[AuthBridge] Refreshing user data from backend');
        // Add a timestamp to bypass caching
        const response = await get().authFetch(`/auth/me?_t=${Date.now()}`);
        const data = await response.json();
        
        if (data.success && data.data) {
          debugLog('[AuthBridge] User data refreshed successfully');
          get().setAuth(data.data, true, false);
        } else {
          logger.error('[AuthBridge] Failed to refresh user data:', data.message);
        }
      } catch (error) {
        logger.error('[AuthBridge] Error refreshing user data:', error);
      }
    },
    
    // Helper to get the current user timezone with fallback to system timezone
    getUserTimezone: () => {
      return get().user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    },
    
    // Method to wait for auth to be ready
    waitForAuthReady: async () => {
      // If auth is already ready, return immediately
      if (get().isAuthReady) {
        debugLog('[AuthBridge] Auth already ready, no need to wait');
        return;
      }
      
      // If we already have a promise waiting, reuse it to avoid multiple timers
      if (currentWaitPromise) {
        debugLog('[AuthBridge] Reusing existing wait promise');
        return currentWaitPromise;
      }
      
      debugLog('[AuthBridge] Creating new wait promise for auth to be ready');
      
      let attempts = 0;
      const maxAttempts = 100; // Maximum attempts (5 seconds at 50ms intervals)
      
      // Create and store the promise
      currentWaitPromise = new Promise<void>((resolve, reject) => {
        const checkAuthReady = () => {
          attempts++;
          
          if (get().isAuthReady) {
            debugLog('[AuthBridge] Auth is now ready after', attempts, 'attempts');
            currentWaitPromise = null; // Clear the promise once resolved
            resolve();
            return;
          }
          
          if (attempts >= maxAttempts) {
            logger.error('[AuthBridge] Timeout waiting for auth to be ready');
            currentWaitPromise = null; // Clear the promise on failure
            reject(new Error('Timeout waiting for auth to be ready'));
            return;
          }
          
          setTimeout(checkAuthReady, 50);
        };
        
        checkAuthReady();
      });
      
      return currentWaitPromise;
    }
  };
}); 
